import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@emotion/react";
import { useUrlParams } from "hooks/useUrlParams";

import {
  getGuidelinesUrl,
  termsOfServiceUrls,
  privacyPolicyUrls,
} from "constants/links";

const DEFAULT_CONTENT_LINKS = [
  ["my_page", "mypage"],
  ["quests", "quests"],
  ["purchase_ticket", "tickets"],
];

export default function SystemFooter({ contentLinks = DEFAULT_CONTENT_LINKS }) {
  const { soulId, lng, work } = useUrlParams();
  const { t } = useTranslation([work, "common"]);
  const navigate = useNavigate();
  const theme = useTheme();

  const label = (key) =>
    t(`${work}:Footer.${key}`, {
      defaultValue: t(`common:Footer.${key}`, { defaultValue: key }),
    });

  const withLanguage = (path) => {
    const separator = path.includes("?") ? "&" : "?";
    return lng ? `${path}${separator}lng=${lng}` : path;
  };

  const internal = (key, page) => ({
    label: label(key),
    path: `/${work}/${page}?soul_id=${soulId}`,
  });

  const links = [
    { label: label("guidelines"), url: getGuidelinesUrl(work, lng) },
    { label: label("terms_of_service"), url: termsOfServiceUrls[lng] || termsOfServiceUrls.ja },
    { label: label("privacy_policy"), url: privacyPolicyUrls[lng] || privacyPolicyUrls.ja },
    internal("news", "news"),
    { label: label("contact"), url: "https://line.me/R/ti/p/@314jdaww" },
    internal("how_to_play", "how-to-use"),
    ...contentLinks.map(([key, page]) => internal(key, page)),
  ];

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        pt: 5,
        pb: 12,
      }}
    >
      {links.map((link) => (
        <Typography
          key={`${link.label}-${link.path || link.url}`}
          component={link.url ? "a" : "button"}
          href={link.url || undefined}
          target={link.url ? "_blank" : undefined}
          rel={link.url ? "noopener noreferrer" : undefined}
          onClick={
            link.path
              ? () => navigate(withLanguage(link.path))
              : undefined
          }
          sx={{
            border: 0,
            padding: 0,
            background: "transparent",
            textDecoration: "none",
            cursor: "pointer",
            transition: "color 0.2s, transform 0.2s",
            "&:hover": { color: "lightgray", transform: "scale(1.05)" },
            "&:active": { color: "gray", transform: "scale(0.95)" },
            ...theme.components.footer,
          }}
        >
          {link.label}
        </Typography>
      ))}
    </Box>
  );
}
